/**
 * Team V2 Repository - Repository Class Implementation
 *
 * Main repository class with 7 queries + 7 mutations.
 * Uses Apollo Client for GraphQL operations.
 *
 * Backend resolves tenantId from auth context — NOT passed as parameter.
 * Signatures match deploy/router/schemas/core.graphql exactly.
 */

import type { ApolloClient } from '@apollo/client';
import {
  GET_TEAM,
  LIST_TEAMS,
  LIST_TEAMS_BY_TYPE,
  LIST_CHILD_TEAMS,
  GET_TEAM_HIERARCHY,
  LIST_ACTIVE_TEAMS,
  SEARCH_TEAMS,
} from './queries';
import {
  CREATE_TEAM,
  UPDATE_TEAM,
  ARCHIVE_TEAM,
  MOVE_TEAM,
  ADD_STAKEHOLDER,
  REMOVE_STAKEHOLDER,
  UPDATE_TEAM_QUOTAS,
} from './mutations';
import type {
  GqlTeam,
  TeamType,
  TeamArchiveResult,
  CreateTeamInput,
  UpdateTeamInput,
  MoveTeamInput,
  AddStakeholderInput,
  RemoveStakeholderInput,
  UpdateTeamQuotasInput,
} from './types';

/**
 * Team V2 Repository with 7 queries + 7 mutations
 *
 * Repository pattern for Team primitive data access.
 * All operations use network-only fetch policy for consistency.
 */
export class TeamRepositoryV2 {
  constructor(private readonly client: ApolloClient) {}

  // ==========================================================================
  // Queries (7)
  // ==========================================================================

  /** Get team by code. */
  async getTeam(code: string): Promise<GqlTeam | null> {
    const { data } = await this.client.query<Record<string, any>>({
      query: GET_TEAM,
      variables: { code },
      fetchPolicy: 'network-only',
    });
    return data?.getTeam || null;
  }

  /** List all teams for tenant. */
  async listTeams(limit?: number): Promise<GqlTeam[]> {
    const { data } = await this.client.query<Record<string, any>>({
      query: LIST_TEAMS,
      variables: { limit },
      fetchPolicy: 'network-only',
    });
    return data?.listTeams ?? [];
  }

  /** List teams by type (Department, Squad, Project, etc.). */
  async listTeamsByType(teamType: TeamType, limit?: number): Promise<GqlTeam[]> {
    const { data } = await this.client.query<Record<string, any>>({
      query: LIST_TEAMS_BY_TYPE,
      variables: { teamType, limit },
      fetchPolicy: 'network-only',
    });
    return data?.listTeamsByType ?? [];
  }

  /** List child teams by parent ID. */
  async listChildTeams(parentId: string, limit?: number): Promise<GqlTeam[]> {
    const { data } = await this.client.query<Record<string, any>>({
      query: LIST_CHILD_TEAMS,
      variables: { parentId, limit },
      fetchPolicy: 'network-only',
    });
    return data?.listChildTeams ?? [];
  }

  /** Get team hierarchy. */
  async getTeamHierarchy(code: string): Promise<GqlTeam | null> {
    const { data } = await this.client.query<Record<string, any>>({
      query: GET_TEAM_HIERARCHY,
      variables: { code },
      fetchPolicy: 'network-only',
    });
    return data?.getTeamHierarchy || null;
  }

  /** List only active teams. */
  async listActiveTeams(limit?: number): Promise<GqlTeam[]> {
    const { data } = await this.client.query<Record<string, any>>({
      query: LIST_ACTIVE_TEAMS,
      variables: { limit },
      fetchPolicy: 'network-only',
    });
    return data?.listActiveTeams ?? [];
  }

  /** Search teams by query string. */
  async searchTeams(query: string, limit?: number): Promise<GqlTeam[]> {
    const { data } = await this.client.query<Record<string, any>>({
      query: SEARCH_TEAMS,
      variables: { query, limit },
      fetchPolicy: 'network-only',
    });
    return data?.searchTeams ?? [];
  }

  // ==========================================================================
  // Mutations (7)
  // ==========================================================================

  /** Create new team. Backend generates Snowflake ID. */
  async createTeam(input: CreateTeamInput): Promise<GqlTeam> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: CREATE_TEAM,
      variables: { input },
    });
    return data?.createTeam ?? null;
  }

  /** Update existing team. Append-only versioning. */
  async updateTeam(code: string, input: UpdateTeamInput): Promise<GqlTeam> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: UPDATE_TEAM,
      variables: { code, input },
    });
    return data?.updateTeam ?? null;
  }

  /**
   * Archive team (append-only, replaces deleteTeam).
   * Sets active=false with full audit trail. Mandatory reason for compliance.
   */
  async archiveTeam(code: string, reason: string): Promise<TeamArchiveResult> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: ARCHIVE_TEAM,
      variables: { code, reason },
    });
    return data?.archiveTeam ?? null;
  }

  /** Move team to new parent (re-parent operation). */
  async moveTeam(input: MoveTeamInput): Promise<GqlTeam> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: MOVE_TEAM,
      variables: { input },
    });
    return data?.moveTeam ?? null;
  }

  /** Add stakeholder to team. */
  async addStakeholder(input: AddStakeholderInput): Promise<GqlTeam> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: ADD_STAKEHOLDER,
      variables: { input },
    });
    return data?.addTeamStakeholder ?? null;
  }

  /** Remove stakeholder from team. */
  async removeStakeholder(input: RemoveStakeholderInput): Promise<GqlTeam> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: REMOVE_STAKEHOLDER,
      variables: { input },
    });
    return data?.removeTeamStakeholder ?? null;
  }

  /** Update team quotas. */
  async updateTeamQuotas(input: UpdateTeamQuotasInput): Promise<GqlTeam> {
    const { data } = await this.client.mutate<Record<string, any>>({
      mutation: UPDATE_TEAM_QUOTAS,
      variables: { input },
    });
    return data?.updateTeamQuotas ?? null;
  }
}

/**
 * Create TeamRepositoryV2 instance.
 */
export function createTeamRepositoryV2(
  client: ApolloClient
): TeamRepositoryV2 {
  return new TeamRepositoryV2(client);
}
